import { observer } from 'mobx-react';
import React from 'react';

import { useParams } from 'react-router-dom';

import { CenteredCircularProgress } from 'components/Progress';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { SearchRequest } from 'utils/api/types';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import API from 'utils/api';

import { useStore } from 'utils/hooks/store';

import { UserRole } from 'core/constants';

import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import InitialAvatar from 'components/Avatar/InitialAvatar';
import HighlightedText from 'components/HighlightedText/HighlightedText';

import { ProtectorTypeUserProps } from '../types';
import { StyledOption, StyledUserInfo, StyledUserName, StyledUserEmail } from './styles';

const loadingOption = { uuid: 'loading', name: 'Loading...' } as UserSearchResponse;

const CustomUserDropdownField = ({
  value,
  onChange,
  requiredNotFilled,
  error,
  errorMessage,
  helperText,
  disableInitialLoad,
  licenses,
  userRole,
  excludeUuid,
  size,
  customPlaceholder,
}: Omit<ProtectorTypeUserProps, 'items'> & {
  error?: boolean;
  helperText?: string;
  disableInitialLoad?: boolean;
  licenses?: string;
  userRole?: string;
  excludeUuid?: string;
}): React.ReactElement => {
  const params = useParams<{ publicFormId: string }>();
  // public form id for entities submission
  const { publicFormId } = params;
  const { session } = useStore();

  const request = React.useCallback(
    (searchParams?: SearchRequest) =>
      API.user.searchUsers({ ...searchParams, public_uuid: publicFormId, licenses, role: userRole }),
    [licenses, userRole]
  );

  const {
    results: usersList,
    loading: loadingUsers,
    loadingMore,
    hasMore,
    loadMore,
    searchQuery: searchQueryUsers,
  } = useInfiniteSearch(request);

  const [selectedUser, setSelectedUser] = React.useState<UserSearchResponse | null | undefined>(
    value ? (value as UserSearchResponse) : null
  );
  const [isReloading, setIsReloading] = React.useState(false);

  React.useEffect(() => {
    if (disableInitialLoad) {
      setSelectedUser(value ? (value as UserSearchResponse) : null);
      return;
    }
    if (typeof value === 'string' && value !== '') {
      const fetchData = async (): Promise<void> => {
        try {
          setIsReloading(true);

          const { data } =
            session.user?.role === UserRole.Manager
              ? await API.user.loadUserDataForManagers(value)()
              : await API.user.loadUserData(value)();

          setSelectedUser(data as UserSearchResponse);
        } finally {
          setIsReloading(false);
        }
      };

      fetchData();
    } else {
      setSelectedUser((previous) => previous || null);
    }
  }, []);

  return (
    <InfiniteAutocomplete
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      id="user-dropdown-field"
      fullWidth
      size={size}
      blurOnSelect
      options={selectedUser === undefined || isReloading ? [loadingOption] : usersList}
      value={selectedUser === undefined || isReloading ? loadingOption : selectedUser}
      loading={loadingUsers}
      noOptionsText="-"
      isOptionEqualToValue={(option: UserSearchResponse | null, val: UserSearchResponse | null) =>
        val?.uuid !== undefined && val?.uuid === option?.uuid
      }
      getOptionLabel={(option: UserSearchResponse | null) => option?.name as string}
      renderOption={(props, option: UserSearchResponse, { inputValue }) =>
        option.uuid === loadingOption.uuid ? (
          <StyledOption>
            <CenteredCircularProgress />
          </StyledOption>
        ) : (
          <StyledOption {...props}>
            <InitialAvatar name={option.name} size="small" />
            <StyledUserInfo>
              <StyledUserName>
                <HighlightedText text={option.name} query={inputValue} />
              </StyledUserName>
              {option.email && (
                <StyledUserEmail>
                  <HighlightedText text={option.email} query={inputValue} />
                </StyledUserEmail>
              )}
            </StyledUserInfo>
          </StyledOption>
        )
      }
      getOptionDisabled={(option: UserSearchResponse) =>
        option.uuid === loadingOption.uuid || option.uuid === selectedUser?.uuid || option.uuid === excludeUuid
      }
      renderInput={(inputParams) => (
        <StyledTextField
          {...inputParams}
          placeholder={customPlaceholder ?? 'Select user'}
          value={selectedUser?.name || ''}
          onChange={(e) => {
            searchQueryUsers(e.target.value);
          }}
          error={requiredNotFilled || error}
          helperText={errorMessage || helperText}
        />
      )}
      onChange={(_, rawValue: UserSearchResponse | null) => {
        setSelectedUser(rawValue);
        onChange(rawValue ? { uuid: rawValue.uuid, name: rawValue.name } : null);
      }}
    />
  );
};

export default observer(CustomUserDropdownField);
